import {
  createColumnHelper,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";

import {
  Body,
  Button,
  Heading,
  Icon,
  TableData,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  useToast,
} from "@elaniintc/goes-design-system";
import TablePaginantion from "@/components/common/table/pagination";
import type { ResolutionType } from "@/schemas/digital-files/digital-file";
import { useEffect, useMemo, useState } from "react";
import { usePageSortFilters } from "@/hooks/use-page-fs";
import {
  useGetResolutions,
  useDeleteResolution,
} from "@/queries/digital-files/registries.api";
import { usePagination } from "@/hooks/usePagination";
import ConfirmationModalBase from "@/components/common/modals/confirmation-modal-base";
import dayjs from "dayjs";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { sortResolutions } from "../../utils";
import { FileLink } from "@/components/common/file-link";

const columnHelper = createColumnHelper<ResolutionType>();

const createColumns = (
  onDelete: (id: string, name: string) => void,
  canDelete: boolean,
) =>
  [
    columnHelper.accessor("display_name", {
      id: "name",
      header: "Nombre de resolución",
      sortUndefined: "last",
      meta: {
        className:
          "max-w-[80px] sm:max-w-[250px] text-ellipsis overflow-hidden font-semibold",
      },
    }),
    columnHelper.accessor("display_code", {
      header: "ID Resolución",
      sortUndefined: "last",
      meta: {
        className: "sm:max-w-none text-ellipsis overflow-hidden",
      },
    }),
    columnHelper.accessor("formality_request", {
      header: "Solicitud Asociada",
      sortUndefined: "last",
      meta: {
        className:
          "hidden sm:table-cell truncate max-w-[80px] sm:max-w-[250px] text-ellipsis",
      },
      cell: (row) => {
        const formality = row.row?.original?.formality_request;
        if (formality) {
          return formality.name;
        }
        return <div>{row?.row?.original.description || ""}</div>;
      },
    }),

    columnHelper.accessor("display_date", {
      id: "elapsed_days",
      header: "Fecha Resolución",
      meta: {
        className: "hidden xs:table-cell",
      },
      sortUndefined: "first",
      cell: (row) => {
        const display_date = row.row?.original?.display_date;
        if (!display_date || !dayjs(display_date).isValid())
          return <div>-</div>;

        return (
          <div className="flex items-center justify-between">
            <div className="flex items-center capitalize">
              {dayjs(display_date).format("MMM D, YYYY | hh:mm A")}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("files", {
      header: "Archivos Resolución",
      sortUndefined: "last",
      meta: {
        className: "sm:max-w-none text-ellipsis overflow-hidden",
      },
      cell: (info) => {
        const row = info.getValue();
        if (row && Array.isArray(row) && row.length > 0) {
          return (
            <div className="flex flex-wrap gap-1">
              {row.map((file) => (
                <FileLink key={file.id} file={file} size="default" />
              ))}
            </div>
          );
        }
        return "";
      },
    }),

    columnHelper.display({
      id: "actions",
      header: "",
      enableSorting: false,
      cell: (info) => {
        const resolution = info.row.original;
        return (
          <div className="w-full flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="opciones"
                  disabled={!canDelete}
                >
                  <Icon type="fas" name="ellipsis-v" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="right"
                align="start"
                className=" border-neutral-silent bg-neutral-object"
              >
                {canDelete && (
                  <DropdownMenuItem
                    onClick={() => onDelete(resolution.id, resolution.name)}
                    className="hover:bg-neutral-object-accent-silent font-normal text-body cursor-pointer border-0"
                  >
                    <span className="text-error">Eliminar</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    }),
  ] as ColumnDef<ResolutionType>[];

interface DigitalResolutionTableProps {
  registryId?: string;
  searchString?: string;
  queryFilters?: object;
}
const DigitalResolutionTable = ({
  registryId,
  searchString,
  queryFilters,
}: DigitalResolutionTableProps) => {
  const { showToast } = useToast();
  const [modalMode, setModalMode] = useState<"idle" | "delete">("idle");
  const [selectedResolution, setSelectedResolution] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);

  const { setPage, setSearchInput, query } = usePageSortFilters({
    defaultOrderField: "!updated_at",
  });
  const { data, isLoading } = useGetResolutions(
    registryId,
    {
      ...queryFilters,
      ...query,
    },
    !!registryId,
  );
  const { mutateAsync: deleteResolution } = useDeleteResolution();
  const { hasPermission } = useAuthorization();
  const canDelete = hasPermission(Permissions.expedient_base_registries.update);
  const {
    currentPage,
    hasNextPage,
    hasPreviousPage,
    onNext,
    onPageChange,
    onPrevious,
    pageSize,
    totalItems,
    totalPages,
  } = usePagination({
    links: data?.links,
    meta: data?.meta,
    onPageUpdate: (page) => {
      setPage(page);
    },
  });

  useEffect(() => {
    setSearchInput(searchString ?? "");
  }, [searchString, setSearchInput]);

  const handleDeleteClick = (id: string, name: string) => {
    setSelectedResolution({ id, name });
    setModalMode("delete");
  };

  const handleConfirmDelete = async () => {
    if (!selectedResolution) return;

    try {
      setIsDeleting(true);
      await deleteResolution(selectedResolution.id);
      showToast({
        title: "Resolución eliminada",
        description: `La resolución "${selectedResolution.name}" ha sido eliminada exitosamente.`,
        options: { type: "success" },
      });
      setModalMode("idle");
      setSelectedResolution(null);
    } catch (error) {
      console.error("Error deleting resolution:", error);
      showToast({
        title: "Error al eliminar",
        description: "No se pudo eliminar la resolución. Intente nuevamente.",
        options: { type: "error" },
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseModal = () => {
    if (!isDeleting) {
      setModalMode("idle");
      setSelectedResolution(null);
    }
  };

  const columns = useMemo(
    () => createColumns(handleDeleteClick, canDelete),
    [canDelete],
  );

  const sortedData = useMemo(() => {
    if (!data?.data || sorting.length === 0) return data?.data ?? [];

    const { id, desc } = sorting[0];
    return sortResolutions(data.data, id, desc);
  }, [data?.data, sorting]);

  if (!isLoading && (data?.data?.length ?? 0) === 0) {
    return (
      <div className="flex flex-col flex-auto justify-center items-center border-t border-neutral-quiet py-10 gap-2">
        <Heading variant="h5" color="quiet">
          Sin resoluciones creadas
        </Heading>
        <Body color="quiet" className="text-center">
          Las resoluciones aparecerán aquí tan pronto sean creadas
        </Body>
      </div>
    );
  }

  return (
    <>
      <TableData
        columns={columns}
        data={sortedData}
        isFirstLoading={isLoading}
        stripped
        withInnerBorder={false}
        emptyState={<span></span>}
        wrapperClassName="flex-auto"
        sorting={sorting}
        onSortingChange={setSorting}
      />
      <div className="p-3">
        <TablePaginantion
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          currentPage={currentPage}
          hasNext={hasNextPage}
          hasPrevious={hasPreviousPage}
          onPageChange={onPageChange}
          onNext={onNext}
          onPrevious={onPrevious}
        />
      </div>

      <ConfirmationModalBase
        isOpen={modalMode === "delete"}
        onClose={handleCloseModal}
        onAccept={handleConfirmDelete}
        title="Eliminar resolución"
        description={`¿Estás seguro de que deseas eliminar la resolución "${selectedResolution?.name}"? Esta acción no se puede deshacer.`}
        acceptText="Eliminar"
        acceptVariant="danger-secondary"
        isLoading={isDeleting}
      />
    </>
  );
};

export { DigitalResolutionTable };
